import { useState, useCallback } from 'react';
import { AuthError, Session } from '@supabase/supabase-js';
import { supabase } from '../utils/supabase';
import { getAuthErrorMessage, AuthErrorMessage } from '../utils/AuthErrors';

export type AuthStep = 
    | 'IDLE'
    | 'VALIDATING_CREDENTIALS'
    | 'CHECKING_2FA'
    | 'CREATING_SESSION'
    | 'LOADING_USER_DATA'
    | 'SYNCING_PREFERENCES';

interface AuthState {
    isLoading: boolean;
    currentStep: AuthStep;
    error: AuthErrorMessage | null;
    session: Session | null;
}

interface RememberMeOptions {
    enabled: boolean;
    duration: number; // in days
}

export function useAuthState() {
    const [state, setState] = useState<AuthState>({
        isLoading: false,
        currentStep: 'IDLE',
        error: null,
        session: null
    });

    const updateStep = useCallback((step: AuthStep) => {
        setState(prev => ({
            ...prev,
            currentStep: step
        }));
    }, []);

    const clearError = useCallback(() => {
        setState(prev => ({
            ...prev,
            error: null
        }));
    }, []);

    const signIn = useCallback(async (
        email: string,
        password: string,
        rememberMe: RememberMeOptions = { enabled: false, duration: 7 }
    ) => {
        setState(prev => ({
            ...prev,
            isLoading: true,
            error: null,
            currentStep: 'VALIDATING_CREDENTIALS'
        }));

        try {
            // Validate credentials
            // First set the session configuration
            await supabase.auth.setSession({
                access_token: '',
                refresh_token: ''
            });

            // Then sign in
            const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
                email,
                password
            });

            if (signInError) throw signInError;

            updateStep('CREATING_SESSION');

            // Get session
            const { data: { session }, error: sessionError } = await supabase.auth.getSession();
            
            if (sessionError) throw sessionError;

            updateStep('LOADING_USER_DATA');

            // Get user profile
            const { error: profileError } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', session?.user.id)
                .single();

            if (profileError) throw profileError;

            updateStep('SYNCING_PREFERENCES');

            // Store session if remember me is enabled
            if (rememberMe.enabled) {
                localStorage.setItem('rememberMe', 'true');
                localStorage.setItem('sessionExpiry', String(Date.now() + rememberMe.duration * 24 * 60 * 60 * 1000));
            }

            setState(prev => ({
                ...prev,
                isLoading: false,
                currentStep: 'IDLE',
                session
            }));

            return { success: true, session };
        } catch (error) {
            const authError = error as AuthError;
            const errorMessage = getAuthErrorMessage(authError);

            setState(prev => ({
                ...prev,
                isLoading: false,
                currentStep: 'IDLE',
                error: errorMessage
            }));

            return { success: false, error: errorMessage };
        }
    }, [updateStep]);

    const signOut = useCallback(async () => {
        setState(prev => ({
            ...prev,
            isLoading: true,
            error: null
        }));

        try {
            const { error } = await supabase.auth.signOut();
            if (error) throw error;

            // Clear stored session data
            localStorage.removeItem('rememberMe');
            localStorage.removeItem('sessionExpiry');

            setState(prev => ({
                ...prev,
                isLoading: false,
                session: null
            }));

            return { success: true };
        } catch (error) {
            const authError = error as AuthError;
            const errorMessage = getAuthErrorMessage(authError);

            setState(prev => ({
                ...prev,
                isLoading: false,
                error: errorMessage
            }));

            return { success: false, error: errorMessage };
        }
    }, []);

    return {
        ...state,
        signIn,
        signOut,
        clearError
    };
}
